// What stops each wizard step (W1): the screens show these words on the step, and "Send for approval" asks the API,
// which runs the same function again (the mock now, the backend in W2). Also the IVR node's key ports: a node set
// up by the wizard keeps its keys as lines "1 consent_yes" (the kit's inspector edits a list of words), and leaves
// by one port per key (press_1 …) + no_answer; an older node keeps yes / stop / no_answer.
import {
  HEADER_URL,
  IvrKeyAction,
  KEY_ACTION_CHANNEL,
  MAX_IVR_KEYS,
  MAX_IVR_TRIES,
  SENDING_CHANNELS,
} from './campaign-wizard'
import type { CampaignDraftInput, IvrKey, WizardStep } from './campaign-wizard'
import { isPromotional } from './campaigns'
import type { HubLender, HubRoute } from './lender-hub'
import type { PhoneNumber } from './numbers'
import { valueProblems, type Template } from './templates'
import type { VoiceMessage } from './voice-messages'
import { NODE_PORTS, NodeType } from './workflows'

// ---------- the IVR node's keys ----------

export const KEY_LINE = new RegExp(`^([0-9]) (${IvrKeyAction.options.join('|')})$`)
export const keyLine = (key: IvrKey) => `${key.digit} ${key.action}`
export const keyPort = (digit: string) => `press_${digit}`
export const NO_ANSWER_PORT = 'no_answer'

/** The keys of a node's cfg.keys lines; a line that is not "digit action" is left out (the checks say so). */
export function keysOf(cfg: Record<string, unknown>): IvrKey[] {
  const lines = Array.isArray(cfg.keys) ? cfg.keys : []
  return lines.flatMap((line) => {
    const match = KEY_LINE.exec(String(line).trim())
    return match ? [{ digit: match[1], action: match[2] } as IvrKey] : []
  })
}

/** Does this IVR node use keys (the wizard's set-up) rather than the older yes / stop digits? */
export const usesKeys = (cfg: Record<string, unknown>) => Array.isArray(cfg.keys) && cfg.keys.length > 0

/** The ports of an IVR node with keys: one per key that leaves the call ("again" plays the message again). */
export function ivrKeyPorts(keys: IvrKey[]): string[] {
  return [...keys.filter((k) => k.action !== 'again').map((k) => keyPort(k.digit)), NO_ANSWER_PORT]
}

/** The ports any node leaves by, from its type and settings (the canvas, the checks and the engine agree). */
export function portsOf(node: { type: string; cfg: Record<string, unknown> }): readonly string[] {
  if (node.type === NodeType.enum['campaign.ivr_call'] && usesKeys(node.cfg))
    return ivrKeyPorts(keysOf(node.cfg))
  const type = NodeType.safeParse(node.type)
  return type.success ? NODE_PORTS[type.data] : []
}

/** Why a list of keys cannot be used; empty = it can. */
export function keyProblems(keys: IvrKey[], ticked: readonly string[]): string[] {
  const problems: string[] = []
  const digits = keys.map((k) => k.digit)
  if (!keys.length) problems.push('Set up at least one key.')
  if (keys.length > MAX_IVR_KEYS) problems.push(`At most ${MAX_IVR_KEYS} keys.`)
  if (new Set(digits).size !== digits.length) problems.push('Each digit can be used only once.')
  if (!keys.some((k) => k.action === 'stop'))
    problems.push('One key must be "Stop" (law and WhatsApp quality).')
  if (keys.filter((k) => k.action === 'consent_yes').length > 1)
    problems.push('Only one key can be "Consent yes".')
  if (keys.some((k) => KEY_ACTION_CHANNEL[k.action] && !ticked.includes(KEY_ACTION_CHANNEL[k.action]!)))
    problems.push('A key sends to a channel that is not ticked in step 1: change that key.')
  return problems
}

// ---------- the wizard's steps ----------

/** What the checks need to know about the portal's data (the screens fetch it; the API has it). */
export type FactNumber = Pick<
  PhoneNumber,
  'id' | 'channel' | 'display' | 'status' | 'quality' | 'series' | 'daily_limit' | 'waba_id'
>
export type FactTemplate = Pick<
  Template,
  'id' | 'channel' | 'name' | 'status' | 'body' | 'category' | 'header_image' | 'for_campaigns' | 'waba_id'
>

export interface WizardFacts {
  numbers: FactNumber[]
  templates: FactTemplate[]
  voiceMessages: Pick<VoiceMessage, 'id' | 'status' | 'wording_version'>[]
  imageIds: string[]
  audienceIds: string[]
  batchIds: string[]
  groupIds: string[]
  routes: Pick<HubRoute, 'id' | 'lender_slug' | 'kind'>[]
  lenders: Pick<HubLender, 'slug' | 'name' | 'link_url'>[]
  /** IST now, to refuse a "later" start in the past */
  now: string
}

/** A number a campaign may tick: never gone or still being added, never a low-quality WhatsApp number, and a
 *  promotional campaign calls only from 140-series numbers. */
export function numberPickable(number: FactNumber, promotional: boolean): boolean {
  if (number.status === 'gone' || number.status === 'adding') return false
  if (number.channel === 'whatsapp' && number.quality === 'low') return false
  return !(number.channel === 'ivr' && promotional && number.series !== 'promo_140')
}

const CHANNEL_WORD = { ivr: 'IVR', whatsapp: 'WhatsApp', sms: 'SMS' } as const

function basicsProblems(draft: CampaignDraftInput): string[] {
  const problems: string[] = []
  if (draft.name.trim().length < 3) problems.push('Give the campaign a name (at least 3 letters).')
  if (!draft.channels.some((c) => SENDING_CHANNELS.includes(c)))
    problems.push('Tick IVR, WhatsApp or SMS: the lender alone is not a campaign.')
  return problems
}

function numbersProblems(draft: CampaignDraftInput, facts: WizardFacts): string[] {
  const problems: string[] = []
  const promotional = isPromotional(draft.kind)
  for (const channel of ['ivr', 'whatsapp', 'sms'] as const) {
    const picked = draft.numbers[channel]
    if (!draft.channels.includes(channel)) {
      if (picked.length)
        problems.push(`${CHANNEL_WORD[channel]} is not ticked in step 1: untick its numbers.`)
      continue
    }
    if (!picked.length) problems.push(`Tick at least one ${CHANNEL_WORD[channel]} number.`)
    for (const id of picked) {
      const number = facts.numbers.find((n) => n.id === id && n.channel === channel)
      if (!number) problems.push(`A ticked ${CHANNEL_WORD[channel]} number is not there any more.`)
      else if (!numberPickable(number, promotional))
        problems.push(
          channel === 'ivr' && promotional
            ? `${number.display}: a promotional campaign calls only from 140-series numbers.`
            : `${number.display} cannot be used now (${number.status}, quality ${number.quality}).`
        )
      else if ((draft.number_caps[id] ?? number.daily_limit) > number.daily_limit)
        problems.push(
          `${number.display}: the daily limit can only be lower than its own (${number.daily_limit}).`
        )
    }
  }
  return problems
}

function ivrProblems(draft: CampaignDraftInput, facts: WizardFacts): string[] {
  const ivr = draft.content.ivr
  if (!ivr) return ['Set up the IVR call.']
  const problems: string[] = []
  const message = facts.voiceMessages.find((m) => m.id === ivr.message_id)
  if (!message) problems.push('Pick a voice message.')
  else if (message.status !== 'approved' || !message.wording_version)
    problems.push('The voice message is not approved (not registered in the CMP).')
  if (!(ivr.retry_hours >= 1 && ivr.retry_hours <= 24)) problems.push('Hours between tries: 1 to 24.')
  if (!(ivr.max_tries >= 1 && ivr.max_tries <= MAX_IVR_TRIES))
    problems.push(`Tries in all: 1 to ${MAX_IVR_TRIES}.`)
  return [...problems, ...keyProblems(ivr.keys, draft.channels)]
}

/** WT1: the ticked WhatsApp numbers that cannot send this template (Meta sends a template only from its own WABA's
 *  numbers). A number or template with no account is not checked here (the engine counts it on the first account). */
export function otherAccountNumbers(
  draft: Pick<CampaignDraftInput, 'numbers'>,
  facts: Pick<WizardFacts, 'numbers'>,
  template: Pick<FactTemplate, 'waba_id'>
): FactNumber[] {
  if (!template.waba_id) return []
  return draft.numbers.whatsapp
    .map((id) => facts.numbers.find((n) => n.id === id && n.channel === 'whatsapp'))
    .filter((n): n is FactNumber => !!n?.waba_id && n.waba_id !== template.waba_id)
}

function whatsappProblems(draft: CampaignDraftInput, facts: WizardFacts): string[] {
  const wa = draft.content.whatsapp
  if (!wa) return ['Set up the WhatsApp message.']
  const template = facts.templates.find((t) => t.id === wa.template_id && t.channel === 'whatsapp')
  if (!template) return ['Pick a WhatsApp template.']
  const problems: string[] = []
  if (template.status !== 'approved') problems.push(`${template.name} is not approved by Meta yet.`)
  if (!template.for_campaigns) problems.push(`${template.name} is marked not for campaigns (WhatsApp templates).`)
  for (const n of otherAccountNumbers(draft, facts, template))
    problems.push(
      `${n.display} is on another WhatsApp account than ${template.name}: untick it (step 2) or pick that account's template.`
    )
  problems.push(...valueProblems(template.body, wa).map((p) => p.message + '.'))
  if (wa.button_value === 'fixed_text' && !/^[A-Za-z0-9._~/-]{1,60}$/.test(wa.button_text?.trim() ?? ''))
    problems.push('Button: type its fixed text (letters, digits, - _ . / only).')
  // campaign links (owner 2026-11-05): the short link opens a journey or an App admin link, never both
  if (wa.link_journey && wa.link_campaign) problems.push('Pick a journey or an App admin link, not both.')
  if (wa.button_value === 'lender_link' && !draft.channels.includes('lender'))
    problems.push("Button: the lender's link needs the Lender step (tick it in step 1).")
  // PD64: the lender's reference comes from the Lender step's answer (a Hub route, not "Lender link only")
  const usesRef =
    wa.button_value === 'lender_ref' ||
    [wa.value_1, wa.value_2, wa.value_3, wa.value_4, wa.value_5].includes('lender_ref')
  if (usesRef && !(draft.channels.includes('lender') && draft.content.lender?.mode === 'route'))
    problems.push("The lender's reference needs the Lender step calling a Hub route (steps 1 and 3).")
  // W2: a template made without an image header gets none (Meta would refuse it): its header choice is not used
  if (template.header_image === null) return problems
  // '' : Meta's picture is not copied yet (Sync copies it; Meta does not fetch its own sample link for a send)
  if (wa.header_source === 'template' && template.header_image === '')
    problems.push(`Header: no copy of ${template.name}'s picture yet: press Sync on the Templates page, or pick an image or a link.`)
  if (wa.header_source === 'url' && !HEADER_URL.test(wa.header_url.trim()))
    problems.push('Header: a public https link to a JPG, PNG or WebP image.')
  if (wa.header_source === 'image' && !facts.imageIds.includes(wa.header_image_id))
    problems.push('Header: pick an image (Images page).')
  return problems
}

function lenderProblems(draft: CampaignDraftInput, facts: WizardFacts): string[] {
  const lender = draft.content.lender
  const found = lender && facts.lenders.find((l) => l.slug === lender.lender_slug)
  if (!lender || !found) return ['Pick a lender.']
  if (lender.mode === 'link_only')
    return found.link_url ? [] : [`${found.name} has no link in the Lender Hub: pick a route instead.`]
  const route = facts.routes.find((r) => r.id === lender.route_id && r.lender_slug === found.slug)
  if (!route) return ['Pick the lender route (dedupe, prefill or link).']
  return route.kind ? [] : ['That route has no kind yet: pick one under Vendors → Lender Hub.']
}

function smsProblems(draft: CampaignDraftInput, facts: WizardFacts): string[] {
  const template = facts.templates.find((t) => t.id === draft.content.sms?.template_id && t.channel === 'sms')
  if (!template) return ['Pick an SMS (DLT) template.']
  return template.status === 'approved' ? [] : [`${template.name} is not approved yet.`]
}

function contentProblems(draft: CampaignDraftInput, facts: WizardFacts): string[] {
  const has = (channel: string) => draft.channels.includes(channel as never)
  return [
    ...(has('ivr') ? ivrProblems(draft, facts) : []),
    ...(has('whatsapp') ? whatsappProblems(draft, facts) : []),
    ...(has('lender') ? lenderProblems(draft, facts) : []),
    ...(has('sms') ? smsProblems(draft, facts) : []),
  ]
}

function audienceProblems(draft: CampaignDraftInput, facts: WizardFacts): string[] {
  if (!draft.audience_id === !draft.schedule) return ['Pick an audience, or a batch / group on a schedule.']
  if (draft.audience_id && !facts.audienceIds.includes(draft.audience_id))
    return ['The audience is not there any more.']
  const schedule = draft.schedule
  if (
    schedule &&
    !(schedule.source === 'batch' ? facts.batchIds : facts.groupIds).includes(schedule.source_id)
  )
    return [`Pick a ${schedule.source}.`]
  return []
}

/** The steps 1–4 choices the canvas is drawn from; when they change, the canvas must be built again. Only words and
 *  numbers (the keys as "3 consent_yes" lines): the backend stores objects in its own key order. */
export function canvasSteps(draft: CampaignDraftInput): string {
  const { channels, content } = draft
  return JSON.stringify([
    [...channels].sort(),
    content.ivr && [content.ivr.keys.map(keyLine), content.ivr.max_tries],
    content.whatsapp?.template_id ?? null,
    content.lender && [content.lender.mode, content.lender.lender_slug, content.lender.route_id],
    content.sms?.template_id ?? null,
  ])
}

function canvasProblems(draft: CampaignDraftInput): string[] {
  if (!draft.document) return ['Pick a workflow in step 5.']
  if (draft.canvas_steps !== canvasSteps(draft))
    return ['Steps 1–4 changed after the workflow was loaded: load it again in step 5.']
  return []
}

function scheduleProblems(draft: CampaignDraftInput, facts: WizardFacts): string[] {
  if (draft.schedule && draft.start_mode !== 'daily')
    return ['A batch or group runs on its schedule: pick "Every day / once".']
  if (!draft.schedule && draft.start_mode === 'daily') return ['"Every day" needs a batch or group (step 4).']
  if (draft.start_mode === 'later' && (!draft.start_at || draft.start_at <= facts.now))
    return ['Pick a date and hour in the future.']
  return []
}

/** Every step's problems; a step is complete when its list is empty. */
export function stepProblems(draft: CampaignDraftInput, facts: WizardFacts): Record<WizardStep, string[]> {
  return {
    basics: basicsProblems(draft),
    numbers: numbersProblems(draft, facts),
    content: contentProblems(draft, facts),
    audience: audienceProblems(draft, facts),
    canvas: canvasProblems(draft),
    schedule: scheduleProblems(draft, facts),
  }
}
